import { lazy, Suspense } from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import { userRoles } from "./constants"
import Smartlook from "smartlook-client"
import { useSelector } from "react-redux"
import { getToken, getUser, isSuperAdmin, isEmployeeUser } from "./store/auth/auth.selectors"
import Layout from "./components/Layout"
import LoadingSpinner from "./components/common/Loading";
import { rememberPostLoginRedirect } from "./helpers/postLoginRedirect"
import AdminWrapper from "./admin-panel/AdminWrapper"

const Overview = lazy(() => import("./pages/Overview"))
const Settings = lazy(() => import("./pages/Settings"))
const ConnectAi = lazy(() => import("./pages/ConnectAi"))
const Documents = lazy(() => import("./pages/Documents"))
const Profile = lazy(() => import("./pages/Profile"))
const Employees = lazy(() => import("./pages/Employees"))
const EmployeesWriteUp = lazy(() => import("./pages/EmployeesWriteUp"))
const Leaderboard = lazy(() => import("./pages/Leaderboard"))
const AppPage = lazy(() => import("./pages/AppPage"))
const PTORequests = lazy(() => import("./pages/PTORequests"))

// run payroll
const RunPayroll = lazy(() => import("./pages/RunPayroll"))

const RunPayrollPreview = lazy(() => import("./pages/RunPayrollPreview"))
const RunPayrollStatement = lazy(() => import("./pages/RunPayrollStatement"))
const RunPayrollProcess = lazy(() => import("./pages/RunPayrollProcess"))

const Expenses = lazy(() => import("./pages/Expenses"))
const ExpensesPreview = lazy(() => import("./pages/ExpensesPreview"))
const EmployeeStats = lazy(() => import("./pages/EmployeeStats"))
const EmployeeReport = lazy(() => import("./pages/EmployeeReportPage"))
const PublicEmployeeWriteUp = lazy(() => import("./pages/EmployeeWriteUp"))
const PublicEmployeeSchedule = lazy(() => import("./pages/PublicEmployeeScheduleReport"))
const PublicEmployeeScheduleConfirm = lazy(() => import("./pages/PublicEmployeeScheduleConfirm"))
const PublicEmployeeDocument = lazy(() => import("./pages/EmployeeDocument"))
const PublicPtoRequestAction = lazy(() => import("./pages/PublicPtoRequestAction"))

// Schedule
const Schedule = lazy(() => import("./pages/Schedule"))
const SchedulePreview = lazy(() => import("./pages/SchedulePreviewPage"))

// Fleet
const Vehicles = lazy(() => import("./pages/Vehicles"))
const VehicleDetail = lazy(() => import("./pages/VehicleDetail"))
const Inventory = lazy(() => import("./pages/Inventory"))
const InventoryDetail = lazy(() => import("./pages/InventoryDetail"))
const Tasks = lazy(() => import("./pages/Tasks"))


// admin
const Insights = lazy(() => import("./admin-panel/pages/Insights"))
const Coupons = lazy(() => import("./admin-panel/pages/Coupons"))
const SuperAdminOrganizations = lazy(() => import("./admin-panel/pages/SuperAdminOrganizations"))
const PendingRequests = lazy(() => import("./admin-panel/pages/PendingRequests"))
const ProviderNameFields = lazy(() => import("./admin-panel/pages/ProviderNameFields"))
const ProviderStructureFields = lazy(() => import("./admin-panel/pages/ProviderStructureFields"))
const ServiceTypes = lazy(() => import("./admin-panel/pages/ServiceTypes"))
const PushNotifications = lazy(() => import("./admin-panel/pages/PushNotifications"))
const Holidays = lazy(() => import("./admin-panel/pages/Holidays"))
const ShortLinks = lazy(() => import("./admin-panel/pages/ShortLinks"))
const IssueReports = lazy(() => import("./admin-panel/pages/IssueReports"))
const Devices = lazy(() => import("./admin-panel/pages/Devices"))

// other
const OnboardCompanyInfo = lazy(() => import("./pages/OnboardCompanyInfo"))
const Login = lazy(() => import("./pages/Login"))
const SignUp = lazy(() => import("./pages/SignUp"))
const Reset = lazy(() => import("./pages/Reset"))
const SocialiteGoogle = lazy(() => import("./pages/SocialiteGoogle"))
const SocialiteApple = lazy(() => import("./pages/SocialiteApple"))
const EmailVerify = lazy(() => import("./pages/EmailVerify"))
const VerifyEmailNotice = lazy(() => import("./pages/VerifyEmailNotice"))
const Error404Page = lazy(() => import("./pages/Error404Page"))
const ServiceUnavailable = lazy(() => import("./pages/ServiceUnavailable"))
const FleetWageDocs = lazy(() => import("./pages/FleetWageDocs"))
const DriverDashboard = lazy(() => import("./pages/DriverDashboard"))
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"))

const routesArray = [
  {
    pathname: "app",
    component: AppPage
  },
  {
    pathname: "employees",
    component: Employees
  },
  {
    pathname: "driver-dashboard",
    component: DriverDashboard
  },
  {
    pathname: "profile",
    component: Profile
  },
  {
    pathname: "profile/:id",
    component: Profile
  },
  {
    pathname: "employees/:employeeId",
    component: EmployeeStats
  },
  {
    pathname: "employee-write-up",
    component: EmployeesWriteUp
  },
  {
    pathname: "leaderboard",
    component: Leaderboard
  },
  {
    pathname: "/",
    component: Overview
  },
  {
    pathname: "run-payroll-preview/:id",
    component: RunPayrollPreview
  },
  {
    pathname: "run-payroll-preview/:payrollId/:statementId",
    component: RunPayrollStatement
  },
  {
    pathname: "run-payroll",
    component: RunPayroll
  },
  {
    pathname: "run-payroll/:payrollId/process",
    component: RunPayrollProcess
  },
  {
    pathname: "schedule",
    component: Schedule
  },
  {
    pathname: "schedule-preview",
    component: SchedulePreview
  },
  {
    pathname: "settings",
    component: Settings
  },
  {
    pathname: "documents",
    component: Documents
  },
  {
    pathname: "expenses",
    component: Expenses
  },
  {
    pathname: "expenses-preview/:payrollId",
    component: ExpensesPreview
  },
  {
    pathname: "pto-requests",
    component: PTORequests
  },
  {
    pathname: "vehicles",
    component: Vehicles
  },
  {
    pathname: "vehicles/:id",
    component: VehicleDetail
  },
  {
    pathname: "inventory",
    component: Inventory
  },
  {
    pathname: "inventory/:id",
    component: InventoryDetail
  },
  {
    pathname: "tasks",
    component: Tasks
  },
]

const ProtectedRoute = ({ auth, redirectPath = "/login", children }) => {
  if (!auth) {
    if (redirectPath === "/login") {
      rememberPostLoginRedirect()
    }

    return <Navigate to={redirectPath} />
  }

  return children ? children : <Outlet />
}

export default function Routing() {
  const token = useSelector(getToken)
  const curToken = localStorage.getItem("token") || token

  const isUserSuperAdminFromLocalStorage =
    JSON.parse(localStorage.getItem("user") || "{}")?.roles?.filter(
      (el) => el.name === userRoles.SUPER_ADMIN
    ).length > 0
  const isUserSuperAdmin = useSelector(isSuperAdmin)
  const curIsUserSuperAdmin = isUserSuperAdminFromLocalStorage || isUserSuperAdmin

  const isImpersonateUser = localStorage.getItem("impersonateUserToken")
  const loggedUser = useSelector(getUser)
  const isEmployee = useSelector(isEmployeeUser)

  if (process.env.REACT_APP_SMARTLOOK_SITE_ID) {
    const publicPaths = [
      "/employee-report/payroll/",
      "/employee-write-up/",
      "/employee/schedule/",
      "/employee/confirm-schedule/",
      "/time-off-request/",
      // "/employee-documents/",
    ]
    const isPublicPage = publicPaths.some((p) =>
      window.location.pathname.startsWith(p)
    )

    if (isPublicPage) {
      // Don't initialize Smartlook on public pages
    } else {
      Smartlook.init(process.env.REACT_APP_SMARTLOOK_SITE_ID)
      Smartlook.record({
        forms: true,
        ips: true,
        numbers: true,
        emails: true,
        api: true
      })

      if (loggedUser && loggedUser.id) {
        Smartlook.identify(loggedUser.email, {
          user_id: loggedUser.id,
          organization_id: loggedUser.organization?.id,
          organization_name: loggedUser.organization?.name,
          is_subscribed: loggedUser.is_subscribed,
          is_impersonated: !!isImpersonateUser
        })
      }
    }
  }

  return (
    <Routes>
      <Route
        path="503"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ServiceUnavailable />
          </Suspense>
        }
      />

      <Route
        path="docs"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <FleetWageDocs />
          </Suspense>
        }
      />

      <Route
        path="s/:hash"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ShortLinkRedirect />
          </Suspense>
        }
      />

      <Route
        path="s/:hash"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ShortLinkRedirect />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute auth={!curToken} redirectPath="/" />}>
        <Route
          path="email/verify"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <EmailVerify />
            </Suspense>
          }
        />
        <Route
          path="socialite/google/callback"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SocialiteGoogle />
            </Suspense>
          }
        />
        <Route
          path="verify-your-email"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <VerifyEmailNotice />
            </Suspense>
          }
        />
        <Route
          path="socialite/apple/callback"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SocialiteApple />
            </Suspense>
          }
        />
        <Route
          path="login"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="sign-up"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SignUp />
            </Suspense>
          }
        />
        <Route
          path="reset"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Reset />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="*"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <Error404Page />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute auth={curToken && curIsUserSuperAdmin} redirectPath="/" />}>
        <Route
          path="admin/organizations"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <SuperAdminOrganizations />
              </AdminWrapper>
            </Suspense>
          }
        />
        <Route
          path="admin/pending-requests"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <PendingRequests />
              </AdminWrapper>
            </Suspense>
          }
        />
        <Route
          path="admin/insights"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <Insights />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/coupons"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <Coupons />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/service-types"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <ServiceTypes />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/provider-name-fields"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <ProviderNameFields />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/provider-structure-fields"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <ProviderStructureFields />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/push-notifications"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <PushNotifications />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/short-links"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <ShortLinks />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/holidays"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <Holidays />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/short-links"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <ShortLinks />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/issue-reports"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <IssueReports />
              </AdminWrapper>
            </Suspense>
          }
        />

        <Route
          path="admin/devices"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <AdminWrapper>
                <Devices />
              </AdminWrapper>
            </Suspense>
          }
        />
      </Route>

      <Route
        path="employee-report/payroll/:payrollId/:employeeId"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <EmployeeReport />
          </Suspense>
        }
      />

      <Route
        path="employee-write-up/:id"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <PublicEmployeeWriteUp />
          </Suspense>
        }
      />

      <Route
        path="employee/schedule/:employeeId/:scheduleId"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <PublicEmployeeSchedule />
          </Suspense>
        }
      />

      <Route
        path="employee/confirm-schedule/:employeeId/:scheduleId"
        element={
            <Suspense fallback={<div>Loading...</div>}>
                <PublicEmployeeScheduleConfirm />
            </Suspense>
        }
      />

      <Route
        path="employee-documents/:employeeId/:documentId"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <PublicEmployeeDocument />
          </Suspense>
        }
      />

      <Route
        path="time-off-request/:token"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <PublicPtoRequestAction />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute auth={curToken} />}>
        <Route
          path="onboard-company"
          element={
            <Suspense fallback={""}>
              <OnboardCompanyInfo />
            </Suspense>
          }
        />
        <Route
          path="connect-ai"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ConnectAi />
            </Suspense>
          }
        />
          {isEmployee && (
              <Route path="/" element={<Navigate to="/driver-dashboard" replace />} />
          )}
          {(isEmployee
                  ? routesArray.filter((el) =>
                      el.pathname === 'tasks' ||
                      ["profile", "settings", "driver-dashboard"].includes(el.pathname)
                    )
                  : routesArray
          ).map((el, index) => {
          const Component = el.component
          return (
            <Route
              key={index}
              path={el.pathname}
              element={
                <Suspense fallback={""}>
                  <Layout
                    pathname={el.pathname}
                    isWageCalculatorPage={[
                      "run-payroll-preview/:id",
                      "run-payroll-preview/:payrollId/:statementId",
                      "run-payroll"
                    ].includes(el.pathname)}>
                    <Component />
                  </Layout>
                </Suspense>
              }
            />
          )
        })}
        {isEmployee && (
          <Route path="*" element={<Navigate to="/driver-dashboard" />} />
        )}
      </Route>
    </Routes>
  )
}
